import { createApi } from '@reduxjs/toolkit/query/react';
import { client } from '../../api/NestClient';
import { nestBaseQuery } from '../../utils/nestBaseQuery';
import type { components, paths } from '../../api/types/api';

type InvoiceSettingsByTypeResponseDto =
  paths['/api/v1/settings/invoice-settings']['get']['responses']['200']['content']['application/json'];
type UpdateInvoiceSettingsResponseDto =
  paths['/api/v1/settings/invoice-settings']['patch']['responses']['200']['content']['application/json'];
type InvoiceSettingsResponseDto = components['schemas']['InvoiceSettingsResponseDto'];
type UpdateInvoiceSettingsDto = components['schemas']['UpdateInvoiceSettingsDto'];
type UpdateInvoiceSettingsRequestDto = components['schemas']['UpdateInvoiceSettingsRequestDto'];

const DTO_TO_LEGACY_KEY = {
  typeCd: 'typecd',
  invoiceHeading: 'invoiceheading',
  signatureText: 'signaturetext',
  declarationAtFooter: 'declarationatfooter',
  termsAndCondition: 'termsandcondition',
  declarationAtFooterFlag: 'declarationatfooterflag',
  termsAndConditionsFlag: 'termsandconditionsflag',
  companyName: 'companyname',
  companyCin: 'companycin',
  invoiceNumber: 'invoicenumber',
  companyAddress: 'companyaddress',
  companyLogo: 'companylogo',
  invoiceDate: 'invoicedate',
  companyContactNumber: 'companycontactnumber',
  companyEmail: 'companyemail',
  paymentMode: 'paymentmode',
  companyGstin: 'companygstin',
  companyWebsite: 'companywebsite',
  creditPeriod: 'creditperiod',
  companyPan: 'companypan',
  companyBankDetails: 'companybankdetails',
  paymentDueDate: 'paymentduedate',
  customerLegalName: 'customerlegalname',
  customerGstin: 'customergstin',
  lutBondDetails: 'lutbonddetails',
  customerDisplayName: 'customerdisplayname',
  placeOfSupply: 'placeofsupply',
  dispatchDetails: 'dispatchdetails',
  customerBillingAddress: 'customerbillingaddress',
  customerContactPerson: 'customercontactperson',
  customerContactNumber: 'customercontactnumber',
  customerName: 'customername',
  ewayBillDetails: 'ewaybilldetails',
  customerShippingAddress: 'customershippingaddress',
  customerOrderNoAndDate: 'customerordernoanddate',
  einvoiceDetails: 'einvoicedetails',
  itemWiseDiscount: 'itemwisediscount',
  hsnSacCode: 'hsnsacode',
  showRoundOff: 'showroundoff',
  hsnSaSummaryTable: 'hsnsasummarytable',
  invoiceDescription: 'invoicedescription',
  regularInvoiceSignatureFileName: 'regularinvoicesignaturefilename',
  posInvoiceSignatureFileName: 'posinvoicesignaturefilename',
  defaultSetting: 'defaultsetting',
  bankDetails: 'bankdetails',
  upiId: 'upi_id',
  upiQrEnabled: 'upiqrenabled',
  acknowledgement: 'acknowledgement',
  acknowledgementFlag: 'acknowledgementflag',
  bosInvoiceHeading: 'bosinvoiceheading',
  bankName: 'bank_name',
  branch: 'branch',
  acName: 'ac_name',
  acNo: 'ac_no',
  ifsc: 'ifsc',
  swiftCode: 'swift_code',
} as const satisfies Record<keyof InvoiceSettingsResponseDto, string>;

type DtoKey = keyof typeof DTO_TO_LEGACY_KEY;

type LegacyInvoiceSettings = {
  [K in DtoKey as (typeof DTO_TO_LEGACY_KEY)[K]]: InvoiceSettingsResponseDto[K];
};

type LegacyInvoiceSettingsForUpdate = {
  [K in keyof LegacyInvoiceSettings]?: NonNullable<LegacyInvoiceSettings[K]>;
};

function mapSingleDtoToLegacy(settings: InvoiceSettingsResponseDto): LegacyInvoiceSettings {
  return {
    typecd: settings.typeCd,
    invoiceheading: settings.invoiceHeading,
    signaturetext: settings.signatureText,
    declarationatfooter: settings.declarationAtFooter,
    termsandcondition: settings.termsAndCondition,
    declarationatfooterflag: settings.declarationAtFooterFlag,
    termsandconditionsflag: settings.termsAndConditionsFlag,
    companyname: settings.companyName,
    companycin: settings.companyCin,
    invoicenumber: settings.invoiceNumber,
    companyaddress: settings.companyAddress,
    companylogo: settings.companyLogo,
    invoicedate: settings.invoiceDate,
    companycontactnumber: settings.companyContactNumber,
    companyemail: settings.companyEmail,
    paymentmode: settings.paymentMode,
    companygstin: settings.companyGstin,
    companywebsite: settings.companyWebsite,
    creditperiod: settings.creditPeriod,
    companypan: settings.companyPan,
    companybankdetails: settings.companyBankDetails,
    paymentduedate: settings.paymentDueDate,
    customerlegalname: settings.customerLegalName,
    customergstin: settings.customerGstin,
    lutbonddetails: settings.lutBondDetails,
    customerdisplayname: settings.customerDisplayName,
    placeofsupply: settings.placeOfSupply,
    dispatchdetails: settings.dispatchDetails,
    customerbillingaddress: settings.customerBillingAddress,
    customercontactperson: settings.customerContactPerson,
    customercontactnumber: settings.customerContactNumber,
    customername: settings.customerName,
    ewaybilldetails: settings.ewayBillDetails,
    customershippingaddress: settings.customerShippingAddress,
    customerordernoanddate: settings.customerOrderNoAndDate,
    einvoicedetails: settings.einvoiceDetails,
    itemwisediscount: settings.itemWiseDiscount,
    hsnsacode: settings.hsnSacCode,
    showroundoff: settings.showRoundOff,
    hsnsasummarytable: settings.hsnSaSummaryTable,
    invoicedescription: settings.invoiceDescription,
    regularinvoicesignaturefilename: settings.regularInvoiceSignatureFileName,
    posinvoicesignaturefilename: settings.posInvoiceSignatureFileName,
    defaultsetting: settings.defaultSetting,
    bankdetails: settings.bankDetails,
    upi_id: settings.upiId,
    upiqrenabled: settings.upiQrEnabled,
    acknowledgement: settings.acknowledgement,
    acknowledgementflag: settings.acknowledgementFlag,
    bosinvoiceheading: settings.bosInvoiceHeading,
    bank_name: settings.bankName,
    branch: settings.branch,
    ac_name: settings.acName,
    ac_no: settings.acNo,
    ifsc: settings.ifsc,
    swift_code: settings.swiftCode,
  };
}

/** Maps UI/legacy settings object to Nest UpdateInvoiceSettingsDto. */
export function mapLegacySettingsToDto(settings: LegacyInvoiceSettingsForUpdate = {}): UpdateInvoiceSettingsDto {
  return {
    invoiceHeading: settings.invoiceheading,
    signatureText: settings.signaturetext,
    declarationAtFooter: settings.declarationatfooter,
    termsAndCondition: settings.termsandcondition,
    declarationAtFooterFlag: settings.declarationatfooterflag,
    termsAndConditionsFlag: settings.termsandconditionsflag,
    companyName: settings.companyname,
    companyCin: settings.companycin,
    invoiceNumber: settings.invoicenumber,
    companyAddress: settings.companyaddress,
    companyLogo: settings.companylogo,
    invoiceDate: settings.invoicedate,
    companyContactNumber: settings.companycontactnumber,
    companyEmail: settings.companyemail,
    paymentMode: settings.paymentmode,
    companyGstin: settings.companygstin,
    companyWebsite: settings.companywebsite,
    creditPeriod: settings.creditperiod,
    companyPan: settings.companypan,
    companyBankDetails: settings.companybankdetails,
    paymentDueDate: settings.paymentduedate,
    customerLegalName: settings.customerlegalname,
    customerGstin: settings.customergstin,
    lutBondDetails: settings.lutbonddetails,
    customerDisplayName: settings.customerdisplayname,
    placeOfSupply: settings.placeofsupply,
    dispatchDetails: settings.dispatchdetails,
    customerBillingAddress: settings.customerbillingaddress,
    customerContactPerson: settings.customercontactperson,
    customerContactNumber: settings.customercontactnumber,
    customerName: settings.customername,
    ewayBillDetails: settings.ewaybilldetails,
    customerShippingAddress: settings.customershippingaddress,
    customerOrderNoAndDate: settings.customerordernoanddate,
    einvoiceDetails: settings.einvoicedetails,
    itemWiseDiscount: settings.itemwisediscount,
    hsnSacCode: settings.hsnsacode,
    showRoundOff: settings.showroundoff,
    hsnSaSummaryTable: settings.hsnsasummarytable,
    invoiceDescription: settings.invoicedescription,
    regularInvoiceSignatureFileName: settings.regularinvoicesignaturefilename,
    posInvoiceSignatureFileName: settings.posinvoicesignaturefilename,
    defaultSetting: settings.defaultsetting,
    bankDetails: settings.bankdetails,
    upiId: settings.upi_id,
    upiQrEnabled: settings.upiqrenabled,
    acknowledgement: settings.acknowledgement,
    acknowledgementFlag: settings.acknowledgementflag,
    bosInvoiceHeading: settings.bosinvoiceheading,
    bankName: settings.bank_name,
    branch: settings.branch,
    acName: settings.ac_name,
    acNo: settings.ac_no,
    ifsc: settings.ifsc,
    swiftCode: settings.swift_code,
  } satisfies Record<keyof UpdateInvoiceSettingsDto, string | number | undefined>;
}

function mapByTypeDtoToLegacyList(response: InvoiceSettingsByTypeResponseDto): LegacyInvoiceSettings[] {
  return [mapSingleDtoToLegacy(response.regularSettings), mapSingleDtoToLegacy(response.posSettings)];
}

export const InvoiceSettingsApi = createApi({
  reducerPath: 'invoiceSettingsApi',
  baseQuery: nestBaseQuery,
  tagTypes: ['InvoiceSettings'],
  endpoints: (builder) => ({
    listInvoiceSettings: builder.query<LegacyInvoiceSettings[], void>({
      query: () => client.GET('/api/v1/settings/invoice-settings'),
      transformResponse: (response: InvoiceSettingsByTypeResponseDto) => mapByTypeDtoToLegacyList(response),
      keepUnusedDataFor: Infinity,
      providesTags: ['InvoiceSettings'],
    }),
    updateInvoiceSettings: builder.mutation<
      UpdateInvoiceSettingsResponseDto,
      UpdateInvoiceSettingsRequestDto
    >({
      query: (body) =>
        client.PATCH('/api/v1/settings/invoice-settings', {
          body,
        }),
      invalidatesTags: ['InvoiceSettings'],
    }),
  }),
});

export const { useListInvoiceSettingsQuery, useUpdateInvoiceSettingsMutation } = InvoiceSettingsApi;
